import { ApiError, type KioskDevice, queryKeys } from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Printer } from 'lucide-react'
import QRCode from 'qrcode'
import { useEffect, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import {
  buildProvisioningPayload,
  MIN_QR_ENROLLMENT_VERSION_CODE,
  printEnrollmentQr,
  type WifiSecurity,
} from '../lib/provisioning-qr.js'
import { type ReleaseManifest, useReleaseManifest } from '../lib/release-manifest.js'

export function DownloadsPage() {
  const { data: manifest, isError } = useReleaseManifest()

  return (
    <Page>
      <PageHeader
        title="Downloads"
        description="The vending terminal app, for installing on a tablet by hand."
      />

      <Card className="max-w-lg">
        <CardContent className="space-y-4 pt-4">
          {isError ? (
            <Alert tone="warning">
              No release has been published yet. Once one is, it appears here automatically.
            </Alert>
          ) : (
            <>
              <div>
                <p className="font-medium">Mevaser Vending</p>
                {manifest ? (
                  <p className="text-sm text-foreground-muted">
                    Version {manifest.versionName} (build {manifest.versionCode})
                  </p>
                ) : null}
              </div>
              {manifest ? (
                <Button asChild variant="primary">
                  <a href={manifest.url} download={`mevaser-vending-${manifest.versionName}.apk`}>
                    <Download /> Download APK
                  </a>
                </Button>
              ) : null}
              {manifest ? (
                <p className="break-all font-identifier text-2xs text-foreground-subtle">
                  sha256 {manifest.sha256}
                </p>
              ) : null}
              <p className="text-xs text-foreground-muted">
                Android will warn about installing from an unknown source the first time — that's
                expected for an app not published on the Play Store. Enable "Install unknown apps"
                for the browser or file manager doing the install. Play Protect may also warn it
                hasn't seen this app's developer before — tap "More details", then "Install anyway".
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {manifest ? <EnrollmentCard manifest={manifest} /> : null}
      <WindowsToolCard />
      {manifest ? <FleetCard manifest={manifest} /> : null}
    </Page>
  )
}

/**
 * Set a factory-reset tablet up as a device-owner kiosk by scanning a QR code
 * on the setup welcome screen (t1183). The code is built here in the browser
 * from the published release manifest — nothing is sent to the server, which
 * matters because the optional Wi-Fi password travels inside it.
 */
function EnrollmentCard({ manifest }: { manifest: ReleaseManifest }) {
  const { can } = useAuth()
  const [ssid, setSsid] = useState('')
  const [password, setPassword] = useState('')
  const [security, setSecurity] = useState<WifiSecurity>('WPA')
  const [qr, setQr] = useState<string | null>(null)

  const supported = manifest.versionCode >= MIN_QR_ENROLLMENT_VERSION_CODE

  useEffect(() => {
    if (!supported) {
      setQr(null)
      return
    }
    let cancelled = false
    const payload = buildProvisioningPayload(manifest, {
      wifi: ssid.trim() ? { ssid, password, security } : undefined,
    })
    QRCode.toDataURL(JSON.stringify(payload), { margin: 2, width: 640, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (!cancelled) setQr(url)
      })
      .catch(() => {
        if (!cancelled) setQr(null)
      })
    return () => {
      cancelled = true
    }
  }, [manifest, supported, ssid, password, security])

  if (!can('kiosks:manage')) return null

  return (
    <Card className="max-w-lg">
      <CardHeader>
        <CardTitle>Set up a tablet by QR code</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        {!supported ? (
          <Alert tone="warning">
            QR setup needs app build {MIN_QR_ENROLLMENT_VERSION_CODE} or newer, and the latest
            published build is {manifest.versionCode}. Use the Windows tool below for now.
          </Alert>
        ) : (
          <>
            <ol className="list-decimal space-y-1 pl-5 text-sm text-foreground-muted">
              <li>Factory reset the tablet. Skip every account sign-in.</li>
              <li>On the first "Welcome" screen, tap the same spot 6 times.</li>
              <li>
                Connect to Wi-Fi if it asks (or fill in the Wi-Fi below so it joins by itself).
              </li>
              <li>Scan the code below. The tablet downloads the app and sets itself up.</li>
              <li>Open the app and pair it from the machine's Tablet tab.</li>
            </ol>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Wi-Fi name (optional)">
                {(props) => (
                  <Input {...props} value={ssid} onChange={(e) => setSsid(e.target.value)} />
                )}
              </Field>
              <Field label="Security">
                {(props) => (
                  <Select value={security} onValueChange={(v) => setSecurity(v as WifiSecurity)}>
                    <SelectTrigger {...props}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="WPA">WPA / WPA2</SelectItem>
                      <SelectItem value="WEP">WEP</SelectItem>
                      <SelectItem value="NONE">Open (no password)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </Field>
              {security !== 'NONE' ? (
                <Field label="Wi-Fi password" className="sm:col-span-2">
                  {(props) => (
                    <Input
                      {...props}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="off"
                    />
                  )}
                </Field>
              ) : null}
            </div>

            {qr ? (
              <div className="space-y-3">
                <img
                  src={qr}
                  alt="Tablet setup QR code"
                  className="mx-auto h-80 w-80 max-w-full rounded-md bg-white"
                />
                <Button variant="secondary" size="sm" onClick={() => printEnrollmentQr(qr)}>
                  <Printer /> Print
                </Button>
              </div>
            ) : null}
            <p className="text-xs text-foreground-muted">
              This code is made in your browser and never sent anywhere. If you add a Wi-Fi
              password, it is inside the code — don't leave a printed copy lying around. The code
              points at build {manifest.versionName} and keeps working after newer releases; the
              tablet updates itself afterwards.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  )
}

/** The laptop route (t1183): one script that installs the app and makes the
 *  tablet a device owner over USB, for a tablet that can't be reset right now. */
function WindowsToolCard() {
  const { can } = useAuth()
  if (!can('kiosks:manage')) return null
  return (
    <Card className="max-w-lg">
      <CardHeader>
        <CardTitle>Set up from a Windows PC</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <ol className="list-decimal space-y-1 pl-5 text-sm text-foreground-muted">
          <li>Factory reset the tablet and skip every account sign-in.</li>
          <li>
            Turn on USB debugging: Settings → About tablet → tap "Build number" 7 times → Developer
            options → USB debugging.
          </li>
          <li>Plug the tablet into the PC and tap Allow on the tablet.</li>
          <li>Download the tool, right-click it, and choose "Run with PowerShell".</li>
        </ol>
        <Button asChild variant="secondary">
          <a href="/tools/provision-tablet.ps1" download="provision-tablet.ps1">
            <Download /> Download Windows tool
          </a>
        </Button>
      </CardContent>
    </Card>
  )
}

/**
 * Which tablets are behind, and a way to push everyone up to the published
 * build at once (t0660) — the per-device version of this lives on each
 * machine's own Devices tab; this is the fleet-wide view. Delivery is
 * heartbeat-carried, not pushed: a request just sets what a tablet should
 * install, and its own next check-in (within about a minute) picks it up.
 */
function FleetCard({ manifest }: { manifest: { versionCode: number; versionName: string } }) {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canManage = can('kiosks:manage')

  const { data: kiosks } = useQuery({
    queryKey: queryKeys.kiosks.list(),
    queryFn: () => api.get<KioskDevice[]>('/kiosks'),
    refetchInterval: 15_000,
    enabled: can('kiosks:read'),
  })

  const requestUpdate = useMutation({
    mutationFn: (input: { id: string; versionCode: number }) =>
      api.post<{ ok: true }>(`/kiosks/${input.id}/request-update`, {
        versionCode: input.versionCode,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.kiosks.all() }),
    onError: (error) =>
      toast.error(
        'Could not request the update',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  if (!can('kiosks:read')) return null

  // Only tablets report a build number at all — the Pi never does.
  const tablets = (kiosks ?? []).filter((k) => k.appVersionCode != null)
  const outdated = tablets.filter(
    (k) => k.appVersionCode != null && manifest.versionCode > k.appVersionCode,
  )
  const updatable = outdated.filter((k) => k.updateTargetVersionCode == null)

  const statusFor = (device: KioskDevice) => {
    if (device.updateTargetVersionCode != null && device.appVersionCode != null) {
      if (device.updateTargetVersionCode > device.appVersionCode) return 'requested' as const
    }
    if (device.appVersionCode != null && manifest.versionCode > device.appVersionCode) {
      return 'available' as const
    }
    return 'current' as const
  }

  const updateAll = async () => {
    const results = await Promise.allSettled(
      updatable.map((device) =>
        requestUpdate.mutateAsync({ id: device.id, versionCode: manifest.versionCode }),
      ),
    )
    const failed = results.filter((r) => r.status === 'rejected').length
    if (failed === 0) {
      toast.success(
        'Updates requested',
        `${results.length} tablet(s) will install on next check-in.`,
      )
    } else {
      toast.toast({
        tone: 'warning',
        title: 'Some requests failed',
        description: `${results.length - failed} succeeded, ${failed} failed.`,
      })
    }
  }

  return (
    <Card className="max-w-lg">
      <CardHeader className="flex-row items-center justify-between gap-2">
        <CardTitle>Fleet</CardTitle>
        {canManage && updatable.length > 0 ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={updateAll}
            disabled={requestUpdate.isPending}
          >
            Update all ({updatable.length})
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {tablets.length === 0 ? (
          <p className="text-sm text-foreground-muted">No tablets have checked in yet.</p>
        ) : (
          tablets.map((device) => {
            const status = statusFor(device)
            return (
              <div key={device.id} className="flex items-center justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <p className="truncate">{device.name}</p>
                  <p className="text-xs text-foreground-muted">
                    build {device.appVersionCode}
                    {device.online ? '' : ' — offline'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {status === 'current' ? (
                    <Badge tone="success">Up to date</Badge>
                  ) : status === 'requested' ? (
                    <Badge tone="info">Requested</Badge>
                  ) : canManage ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        requestUpdate.mutate({ id: device.id, versionCode: manifest.versionCode })
                      }
                    >
                      Install
                    </Button>
                  ) : (
                    <Badge tone="warning">Update available</Badge>
                  )}
                </div>
              </div>
            )
          })
        )}
        <p className="text-xs text-foreground-muted">
          Tablets can also update themselves — turn that on from Settings.
        </p>
      </CardContent>
    </Card>
  )
}
